import { ChartData, ChartConfig, ChartDataset } from './chart.interfaces';
import { ChartCalculationManager } from './chart-calculation.manager';

export class ChartPathManager {
  constructor(private calcManager: ChartCalculationManager) {}

  /**
   * Generate SVG path data for a dataset line
   * Supports datasets with their own labels (ownLabels) for independent time axis
   */
  getPathData(
    data: (number | null)[],
    chartData: ChartData,
    config: ChartConfig,
    dataset?: ChartDataset
  ): string {
    if (!data || data.length === 0) return '';

    const leftPadding = config.padding?.left ?? 50;
    const chartWidth = this.calcManager.getChartWidth(config);

    // Check if this dataset uses its own labels
    const useOwnLabels = dataset?.useOwnLabels && dataset?.ownLabels?.length;
    const ownLabels = dataset?.ownLabels || [];
    const chartLabels = chartData.labels || [];
    const axisId = dataset?.yAxisID; // Get the axis ID from dataset

    const points: ({ x: number; y: number } | null)[] = data.map((value, index) => {
      if (value === null) return null;

      let x: number;
      if (useOwnLabels && chartLabels.length > 0) {
        // Calculate X position based on the dataset's own label mapped to the chart's time axis
        x = this.getXPositionForDate(ownLabels[index], chartLabels, leftPadding, chartWidth);
      } else {
        // Standard calculation based on index
        const labelCount = chartLabels.length || data.length;
        x = leftPadding + (index * (chartWidth / Math.max(labelCount - 1, 1)));
      }

      const y = this.calcManager.getYPosition(value, chartData, config, axisId);
      return { x, y };
    });

    let path = '';
    let lastPointIsPath = false;

    for (const point of points) {
      if (point && !isNaN(point.x) && !isNaN(point.y)) {
        path += lastPointIsPath ? ` L ${point.x} ${point.y}` : ` M ${point.x} ${point.y}`;
        lastPointIsPath = true;
      } else {
        lastPointIsPath = false;
      }
    }

    return path;
  }

  /**
   * Generate SVG path for area band between min and max values
   * Supports datasets with their own labels (ownLabels) for independent time axis
   */
  getAreaPath(
    dataMin: (number | null)[],
    dataMax: (number | null)[],
    chartData: ChartData,
    config: ChartConfig,
    dataset?: ChartDataset
  ): string {
    if (!dataMin || !dataMax || dataMin.length === 0 || dataMax.length === 0) return '';

    const leftPadding = config.padding?.left ?? 50;
    const chartWidth = this.calcManager.getChartWidth(config);

    // Check if this dataset uses its own labels
    const useOwnLabels = dataset?.useOwnLabels && dataset?.ownLabels?.length;
    const ownLabels = dataset?.ownLabels || [];
    const chartLabels = chartData.labels || [];
    const labelCount = chartLabels.length || dataMin.length;
    const axisId = dataset?.yAxisID; // Get the axis ID from dataset

    // Build points for max line (top of area)
    const maxPoints: { x: number; y: number }[] = [];
    for (let i = 0; i < dataMax.length; i++) {
      const value = dataMax[i];
      if (value !== null) {
        let x: number;
        if (useOwnLabels && chartLabels.length > 0) {
          x = this.getXPositionForDate(ownLabels[i], chartLabels, leftPadding, chartWidth);
        } else {
          x = leftPadding + (i * (chartWidth / Math.max(labelCount - 1, 1)));
        }
        const y = this.calcManager.getYPosition(value, chartData, config, axisId);
        if (!isNaN(x) && !isNaN(y)) {
          maxPoints.push({ x, y });
        }
      }
    }

    // Build points for min line (bottom of area)
    const minPoints: { x: number; y: number }[] = [];
    for (let i = 0; i < dataMin.length; i++) {
      const value = dataMin[i];
      if (value !== null) {
        let x: number;
        if (useOwnLabels && chartLabels.length > 0) {
          x = this.getXPositionForDate(ownLabels[i], chartLabels, leftPadding, chartWidth);
        } else {
          x = leftPadding + (i * (chartWidth / Math.max(labelCount - 1, 1)));
        }
        const y = this.calcManager.getYPosition(value, chartData, config, axisId);
        if (!isNaN(x) && !isNaN(y)) {
          minPoints.push({ x, y });
        }
      }
    }

    if (maxPoints.length === 0 || minPoints.length === 0) return '';

    // Create closed path
    let path = `M ${maxPoints[0].x} ${maxPoints[0].y}`;

    for (let i = 1; i < maxPoints.length; i++) {
      path += ` L ${maxPoints[i].x} ${maxPoints[i].y}`;
    }

    for (let i = minPoints.length - 1; i >= 0; i--) {
      path += ` L ${minPoints[i].x} ${minPoints[i].y}`;
    }

    path += ' Z';
    return path;
  }

  /**
   * Calculate X position for a date string based on the chart's time axis
   */
  private getXPositionForDate(
    dateStr: string,
    chartLabels: string[],
    leftPadding: number,
    chartWidth: number
  ): number {
    if (!dateStr || chartLabels.length === 0) return NaN;

    const date = new Date(dateStr);
    const firstDate = new Date(chartLabels[0]);
    const lastDate = new Date(chartLabels[chartLabels.length - 1]);

    const totalDuration = lastDate.getTime() - firstDate.getTime();
    if (totalDuration === 0) return leftPadding;

    const dateOffset = date.getTime() - firstDate.getTime();
    const ratio = dateOffset / totalDuration;

    return leftPadding + (ratio * chartWidth);
  }
}
